"use client";

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import type { UserDTO } from "@chatsystem/common/dtos";

export type SessionUserDTO = UserDTO & {
  companyRole?: "OWNER" | "MEMBER";
};
import {
  resolveApiSessionUser,
  type ApiSessionError,
} from "@/modules/auth/apiSession.client";

type AuthContextValue = {
  me: SessionUserDTO | null;
  loadingMe: boolean;
  errorMe: string | null;
  errorReference: string | null;
  refreshMe: () => Promise<void>;
  setMe: React.Dispatch<React.SetStateAction<SessionUserDTO | null>>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const AUTH_REQUEST_TIMEOUT_MS = 12_000;

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  return fetch(input, {
    ...init,
    signal: AbortSignal.timeout(AUTH_REQUEST_TIMEOUT_MS),
  });
}

async function fetchMe(): Promise<SessionUserDTO> {
  const traceId = crypto.randomUUID();

  const r = await fetchWithTimeout("/api/api-auth/me", {
    method: "GET",
    headers: { "x-trace-id": traceId },
    cache: "no-store",
    credentials: "include",
  });

  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    const err: any = new Error(
      data?.error ?? `Failed to load user (${r.status})`,
    );
    err.status = r.status;
    err.reference = data?.reference ?? traceId;
    throw err;
  }

  return r.json();
}

async function refreshApi(): Promise<boolean> {
  const traceId = crypto.randomUUID();
  const r = await fetchWithTimeout("/api/api-auth/refresh", {
    method: "POST",
    headers: { "x-trace-id": traceId },
    cache: "no-store",
    credentials: "include",
  });
  if (r.ok) return true;
  if (r.status === 401) return false;

  const data = await r.json().catch(() => ({}));
  const error = new Error(
    data?.error ?? "API refresh unavailable",
  ) as ApiSessionError;
  error.status = r.status;
  error.reference = data?.reference ?? traceId;
  throw error;
}

async function exchangeAuthTokens(): Promise<void> {
  const traceId = crypto.randomUUID();
  const r = await fetchWithTimeout("/api/api-auth/exchange", {
    method: "POST",
    headers: { "x-trace-id": traceId },
    cache: "no-store",
    credentials: "include",
  });

  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    const error = new Error(
      data?.error ?? `Exchange failed (${r.status})`,
    ) as ApiSessionError;
    error.status = r.status;
    error.reference = data?.reference ?? traceId;
    throw error;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  const router = useRouter();

  const [me, setMe] = useState<UserDTO | null>(null);
  const [loadingMe, setLoadingMe] = useState(false);
  const [errorMe, setErrorMe] = useState<string | null>(null);
  const [errorReference, setErrorReference] = useState<string | null>(null);
  const statusRef = useRef(status);
  const authGenerationRef = useRef(0);
  const inFlightRef = useRef<{
    generation: number;
    promise: Promise<void>;
  } | null>(null);

  statusRef.current = status;

  const ensureMe = useCallback(
    (generation = authGenerationRef.current) => {
      if (inFlightRef.current?.generation === generation) {
        return inFlightRef.current.promise;
      }

      setLoadingMe(true);
      setErrorMe(null);
      setErrorReference(null);

      const promise = (async () => {
        try {
          const resolution = await resolveApiSessionUser({
            fetchUser: fetchMe,
            refresh: refreshApi,
            exchange: exchangeAuthTokens,
          });
          if (
            authGenerationRef.current === generation &&
            statusRef.current === "authenticated"
          ) {
            setMe(resolution.user);
            if (resolution.recovery !== null) {
              // The protected page can be rendered on the server before the
              // browser has established its API cookies. Re-render the Server
              // Components once after recovery so their authenticated reads use
              // the session that was just created or rotated.
              router.refresh();
            }
          }
        } catch (error: any) {
          if (
            authGenerationRef.current === generation &&
            statusRef.current === "authenticated"
          ) {
            // Keep an already loaded user during a background refresh incident.
            // On initial load, AuthGate renders a bounded retry/reconnect state.
            setErrorMe(error?.message ?? "API_SESSION_UNAVAILABLE");
            setErrorReference(error?.reference ?? null);
            console.warn("Account session refresh failed", {
              status: error?.status ?? null,
              reference: error?.reference ?? null,
            });
          }
        } finally {
          if (authGenerationRef.current === generation) {
            setLoadingMe(false);
          }
        }
      })();

      inFlightRef.current = { generation, promise };
      void promise.finally(() => {
        if (inFlightRef.current?.promise === promise) {
          inFlightRef.current = null;
        }
      });
      return promise;
    },
    [router],
  );

  const refreshMe = useCallback(async () => {
    await ensureMe();
  }, [ensureMe]);

  // React to NextAuth status changes
  useEffect(() => {
    authGenerationRef.current += 1;
    const generation = authGenerationRef.current;

    if (status === "authenticated") {
      void ensureMe(generation);
      return;
    }
    if (status === "unauthenticated") {
      setMe(null);
      setErrorMe(null);
      setErrorReference(null);
      setLoadingMe(false);
    }
  }, [status, ensureMe]);

  useEffect(() => {
    if (status !== "authenticated") return;

    const intervalMs = 5 * 60 * 1000;
    const handleFocus = () => {
      void ensureMe();
    };

    const intervalId = window.setInterval(() => {
      void ensureMe();
    }, intervalMs);

    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [status, ensureMe]);

  const value = useMemo(
    () => ({
      me,
      loadingMe,
      errorMe,
      errorReference,
      refreshMe,
      setMe,
    }),
    [me, loadingMe, errorMe, errorReference, refreshMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
